import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { AI_SECTIONS, type AISectionId } from '../constants';
import {
  BriefingCard,
  LeadScoresCard,
  DealIntelCard,
  CallOptimizerCard,
  NicheCard,
  ScrapeAdvisorCard,
  AnomaliesCard,
} from '../components/sections/AnalyticsSections';
import { AssistantSection, MeetingAssistantSection } from '../components/sections/GenerativeSections';
import { LeadInsightsTool, FollowUpDraftTool, CallCoachTool, IntentParserTool } from '../components/sections/GenerativeTools';
import { CapabilitiesSection } from '../components/sections/CapabilitiesSection';

export function AIWorkspacePage() {
  const [section, setSection] = useState<AISectionId>('overview');

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex items-center gap-3">
        <Sparkles className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-display-sm font-semibold text-on-surface">AI Workspace</h1>
          <p className="text-body-sm text-on-surface-variant">Insights, intelligence and the AI assistant — one workspace.</p>
        </div>
      </div>

      {/* Section nav */}
      <div className="flex flex-wrap gap-1 border-b border-outline pb-2">
        {AI_SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={cn(
              'flex items-center gap-1.5 rounded-DEFAULT px-3 py-1.5 text-data-md font-medium transition-colors',
              section === s.id ? 'bg-primary-container text-on-primary-container' : 'text-on-surface-variant hover:bg-surface-variant',
            )}
          >
            <s.icon className="h-4 w-4" />
            {s.label}
          </button>
        ))}
      </div>

      {/* Section content */}
      <div className="flex-1 overflow-y-auto">
        {section === 'overview' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BriefingCard />
            <DealIntelCard />
          </div>
        )}

        {section === 'assistant' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <AssistantSection />
            <IntentParserTool />
          </div>
        )}

        {section === 'insights' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BriefingCard />
            <AnomaliesCard />
          </div>
        )}

        {section === 'leads' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <LeadScoresCard />
            <div className="flex flex-col gap-4">
              <LeadInsightsTool />
              <FollowUpDraftTool />
            </div>
          </div>
        )}

        {section === 'deals' && <DealIntelCard />}

        {section === 'optimization' && (
          <div className="grid grid-cols-1 gap-4">
            <CallOptimizerCard />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <NicheCard />
              <ScrapeAdvisorCard />
            </div>
            <CallCoachTool />
          </div>
        )}

        {section === 'meeting' && <MeetingAssistantSection />}

        {section === 'settings' && <CapabilitiesSection />}
      </div>
    </div>
  );
}
